import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomModal, CustomInput, CustomSelect, CustomButton, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, LayoutGrid, Pencil, Trash2, List, BarChart, Plus, CornerDownRight, Tag, User, Shield, ChevronDown, ChevronRight, Network, Eye, Clock } from 'lucide-react';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { departmentApi } from '../api/departmentApi';
import { employeeApi } from '../api/employeeApi';
import { workflowApi } from '../api/workflowApi';

// Demo Interface
interface DepartmentLevel {
    id: string;
    name: string;
    role: string;
    parent_id: string | null;
    hierarchy_order: number;
    created_at: string;
    permissions?: {
        read: boolean;
        forward: boolean;
        resolve: boolean;
        reply: boolean;
        reopen: boolean;
    };
    assigned_employee?: {
        user_id: string;
        name: string;
        email: string;
        designation?: string | null;
        departments?: string[];
    };
    sla_hours?: number;
}

const combinedSchema = z.object({
    name: z.string().optional(),
    role: z.string().optional(),
    employee: z.string().optional(),
    sla_hours: z.coerce.number().optional(),
    permissions: z.object({
        read: z.boolean().default(false),
        forward: z.boolean().default(false),
        resolve: z.boolean().default(false),
        reply: z.boolean().default(false),
        reopen: z.boolean().default(false),
    }).optional()
});
type LevelFormData = z.infer<typeof combinedSchema>;

export const ListDepartmentLevels = () => {
    const navigate = useNavigate();
    const { departmentId, typeId } = useParams<{ departmentId: string, typeId: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToastStore();
    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean,
        type: 'step' | 'sublevel',
        parentId?: string,
        isEditing?: boolean,
        selectedItem?: DepartmentLevel | null
    }>({ isOpen: false, type: 'step' });

    const [collapsedSteps, setCollapsedSteps] = useState<Record<string, boolean>>({});
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean, type: 'step' | 'level', item: DepartmentLevel | null }>({ isOpen: false, type: 'step', item: null });
    const [isDeleting, setIsDeleting] = useState(false);
    const [departmentLevels, setDepartmentLevels] = useState<DepartmentLevel[]>([]);
    const [employees, setEmployees] = useState<{ id: string, name: string, photo_url?: string | null, designation?: string | null }[]>([]);
    const [workflowName, setWorkflowName] = useState<string>('');
    const [departmentName, setDepartmentName] = useState<string>('');
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [page, setPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        if (schoolSlug && typeId) {
            fetchWorkflowSteps();
            fetchEmployees();
        }
    }, [schoolSlug, typeId]);

    const fetchEmployees = async () => {
        try {
            const response = await employeeApi.getEmployees(schoolSlug, { page: 1, page_size: 50 });
            if (response.success !== false) {
                const data = response.data as any;
                const empData = Array.isArray(data) ? data : (data?.items || []);
                setEmployees(empData.map((e: any) => ({
                    id: e.id || e.user_id,
                    name: e.user ? `${e.user.first_name} ${e.user.last_name}` : e.name || 'Unknown Employee',
                    photo_url: e.photo_url,
                    designation: e.designation || null
                })));
            }
        } catch (error) {
            console.error('Failed to load employees', error);
        }
    };

    const fetchWorkflowSteps = async () => {
        setIsLoading(true);
        try {
            const response = await workflowApi.getWorkflowSteps(schoolSlug, typeId as string);
            if (response.success !== false) {
                const flattened: DepartmentLevel[] = [];
                let rawData = response.data || (response as any).steps || response;

                // Extract workflow name if available
                if (rawData && typeof rawData === 'object' && (rawData as any).workflow) {
                    setWorkflowName((rawData as any).workflow.name || '');
                    setDepartmentName((rawData as any).workflow.departments?.name || '');
                }

                // If it's the object structure { workflow, steps }, extract steps
                let stepsArray: any[] = [];
                if (Array.isArray(rawData)) {
                    stepsArray = rawData;
                } else if (rawData && typeof rawData === 'object' && Array.isArray((rawData as any).steps)) {
                    stepsArray = (rawData as any).steps;
                }

                if (stepsArray.length > 0) {
                    stepsArray.forEach((step: any, index: number) => {
                        flattened.push({
                            id: step.id,
                            name: step.name,
                            role: step.description || '',
                            parent_id: 'none',
                            hierarchy_order: step.order_index ?? index,
                            created_at: step.created_at || new Date().toISOString(),
                            sla_hours: step.sla_hours
                        });
                        if (step.levels && Array.isArray(step.levels)) {
                            step.levels.forEach((level: any, lIndex: number) => {
                                flattened.push({
                                    id: level.id,
                                    name: level.name,
                                    role: level.description || '',
                                    parent_id: step.id,
                                    hierarchy_order: level.order_index ?? lIndex,
                                    created_at: level.created_at || new Date().toISOString(),
                                    permissions: {
                                        read: level.can_read || false,
                                        forward: level.can_forward || false,
                                        resolve: level.can_resolve || false,
                                        reply: level.can_reply || false,
                                        reopen: level.can_reopen || false
                                    },
                                    assigned_employee: level.assigned_employee
                                    ? {
                                        user_id: level.assigned_employee.user_id,
                                        name: level.assigned_employee.name,
                                        email: level.assigned_employee.email,
                                        designation: level.assigned_employee.designation || null,
                                        departments: level.assigned_employee.departments || []
                                    }
                                    : undefined
                                });
                            });
                        }
                    });
                }
                setDepartmentLevels(flattened);
            } else {
                showToast(response.error || 'Failed to load workflow steps', 'error');
            }
        } catch (error) {
            showToast('Failed to load workflow steps', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const toggleStep = (stepId: string) => {
        setCollapsedSteps(prev => ({
            ...prev,
            [stepId]: !prev[stepId]
        }));
    };

    const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm<LevelFormData>({
        resolver: zodResolver(combinedSchema),
        defaultValues: { permissions: { read: true, forward: false, resolve: false, reply: false, reopen: false } }
    });

    const openModal = (type: 'step' | 'sublevel', parentId?: string, isEditing = false, item: any | null = null) => {
        setFormErrors({});
        if (isEditing && item) {
            if (type === 'step') {
                reset({
                    name: item.name,
                    role: item.role,
                    sla_hours: item.sla_hours || 12,
                });
            } else {
                reset({
                    employee: item.assigned_employee?.user_id || '',
                    permissions: item.permissions || { read: true, forward: false, resolve: false, reply: false, reopen: false }
                });
            }
        } else {
            reset({
                employee: '',
                name: '',
                role: '',
                sla_hours: 12,
                permissions: { read: true, forward: false, resolve: false, reply: false, reopen: false }
            });
        }
        setModalConfig({ isOpen: true, type, parentId, isEditing, selectedItem: item });
    };

    const closeModal = () => {
        setFormErrors({});
        setModalConfig({ isOpen: false, type: 'step' });
    };

    const onSubmit = async (data: LevelFormData) => {
        if (modalConfig.type === 'step') {
            const errors: Record<string, string> = {};
            if (!data.name || data.name.trim().length < 2) {
                errors.name = 'Step name must be at least 2 characters';
            }
            if (!data.role || data.role.trim().length < 1) {
                errors.role = 'Description is required';
            }
            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                return;
            }
            setFormErrors({});
        } else {
            const errors: Record<string, string> = {};
            if (!data.employee) {
                errors.employee = 'Please assign an employee to this level';
            }
            const p = data.permissions;
            const hasPermission = p && (p.read || p.forward || p.resolve || p.reply || p.reopen);
            if (!hasPermission) {
                errors.permissions = 'At least one permission must be selected';
            }
            if (Object.keys(errors).length > 0) {
                setFormErrors(errors);
                return;
            }
            setFormErrors({});
        }

        setIsLoading(true);
        try {
            if (modalConfig.type === 'step') {
                const response = modalConfig.isEditing && modalConfig.selectedItem
                    ? await workflowApi.updateWorkflowStep(schoolSlug, modalConfig.selectedItem.id, {
                        name: data.name as string,
                        description: data.role as string,
                        sla_hours: data.sla_hours
                    } as any)
                    : await workflowApi.createWorkflowStep(schoolSlug, {
                        name: data.name as string,
                        description: data.role as string,
                        sla_hours: data.sla_hours || 12,
                        workflow_id: typeId as string
                    });

                if (response.success !== false) {
                    showToast(`Step ${modalConfig.isEditing ? 'Updated' : 'Added'} Successfully`, 'success');
                    closeModal();
                    fetchWorkflowSteps();
                } else {
                    showToast(response.error || `Failed to ${modalConfig.isEditing ? 'update' : 'add'} step`, 'error');
                }
            } else {
                const payload = {
                    assigned_employee_id: data.employee || '',
                    can_read: data.permissions?.read || false,
                    can_forward: data.permissions?.forward || false,
                    can_resolve: data.permissions?.resolve || false,
                    can_reply: data.permissions?.reply || false,
                    can_reopen: data.permissions?.reopen || false,
                    step_id: modalConfig.parentId as string
                };

                const response = modalConfig.isEditing && modalConfig.selectedItem
                    ? await workflowApi.updateWorkflowLevel(schoolSlug, modalConfig.selectedItem.id, payload)
                    : await workflowApi.createWorkflowLevel(schoolSlug, payload);

                if (response.success !== false) {
                    showToast(`Employee Assignment ${modalConfig.isEditing ? 'Updated' : 'Added'} Successfully`, 'success');
                    closeModal();
                    fetchWorkflowSteps();
                } else {
                    showToast(response.error || `Failed to ${modalConfig.isEditing ? 'update' : 'add'} employee assignment`, 'error');
                }
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (level: DepartmentLevel) => {
        const isStep = level.parent_id === 'none' || level.parent_id === null;
        openModal(isStep ? 'step' : 'sublevel', level.parent_id || undefined, true, level);
    };

    const handleDeleteClick = (level: DepartmentLevel) => {
        const isStep = level.parent_id === 'none' || level.parent_id === null;
        setDeleteModal({
            isOpen: true,
            type: isStep ? 'step' : 'level',
            item: level
        });
    };

    const handleConfirmDelete = async () => {
        if (!deleteModal.item || !schoolSlug) return;

        setIsDeleting(true);
        try {
            const response = deleteModal.type === 'step'
                ? await workflowApi.deleteWorkflowStep(schoolSlug, deleteModal.item.id)
                : await workflowApi.deleteWorkflowLevel(schoolSlug, deleteModal.item.id);

            if (response.success !== false) {
                showToast(`${deleteModal.type === 'step' ? 'Step' : 'Level'} deleted successfully`, 'success');
                fetchWorkflowSteps();
                setDeleteModal({ isOpen: false, type: 'step', item: null });
            } else {
                showToast(response.error || `Failed to delete ${deleteModal.type}`, 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const topLevels = departmentLevels.filter(l => l.parent_id === 'none' || l.parent_id === null);
    const sortedData: DepartmentLevel[] = [];
    topLevels.forEach(topLevel => {
        sortedData.push(topLevel);
        if (!collapsedSteps[topLevel.id]) {
            const children = departmentLevels.filter(l => l.parent_id === topLevel.id);
            sortedData.push(...children);
        }
    });

    const permConfig = [
        { key: 'read'    as const, label: 'Read',    color: '#2563eb', bg: 'rgba(37,99,235,0.1)'  },
        { key: 'forward' as const, label: 'Forward', color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' },
        { key: 'resolve' as const, label: 'Resolve', color: '#059669', bg: 'rgba(5,150,105,0.1)'  },
        { key: 'reply'   as const, label: 'Reply',   color: '#d97706', bg: 'rgba(217,119,6,0.1)'  },
        { key: 'reopen'  as const, label: 'Reopen',  color: '#dc2626', bg: 'rgba(220,38,38,0.1)'  },
    ];

    const columns: CustomTableColumn<DepartmentLevel>[] = [
        {
            header: 'Escalation Steps & Assigned Employees',
            key: 'name',
            width: '42%',
            render: (level) => {
                const isStep = level.parent_id === 'none' || level.parent_id === null;
                const isCollapsed = collapsedSteps[level.id];

                if (isStep) {
                    // ── STEP ROW ──────────────────────────────────────
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                                onClick={(e) => { e.stopPropagation(); toggleStep(level.id); }}
                                style={{
                                    width: '26px', height: '26px', borderRadius: '6px',
                                    background: isCollapsed ? 'var(--bg-tertiary)' : 'var(--accent-primary)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid var(--border-color)',
                                    color: isCollapsed ? 'var(--text-primary)' : 'white',
                                    cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0
                                }}
                                title={isCollapsed ? 'Show employees' : 'Hide employees'}
                            >
                                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                                        {level.name}
                                    </span>
                                </div>
                                {level.role && (
                                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                                        {level.role}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                }

                // ── ASSIGNMENT ROW ────────────────────────────────────
                const emp = level.assigned_employee;
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '2.75rem' }}>
                        <CornerDownRight size={14} style={{ opacity: 0.3, flexShrink: 0 }} />
                        {emp ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                                <div style={{
                                    width: '34px', height: '34px', borderRadius: '50%',
                                    background: 'var(--bg-tertiary)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '0.88rem', fontWeight: 700,
                                    color: 'var(--accent-primary)', border: '2px solid var(--accent-primary)',
                                    flexShrink: 0, opacity: 0.85
                                }}>
                                    {emp.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)', display: 'block' }}>
                                        {emp.name}
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap', margin: '2px 0' }}>
                                        {emp.designation && (
                                            <span style={{
                                                fontSize: '0.67rem', padding: '1px 7px', borderRadius: '10px',
                                                background: 'var(--accent-primary)', color: 'white', fontWeight: 600
                                            }}>
                                                {emp.designation}
                                            </span>
                                        )}
                                        {emp.departments && emp.departments.map((d, i) => (
                                            <span key={i} style={{
                                                fontSize: '0.67rem', padding: '1px 6px', borderRadius: '8px',
                                                background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                                                color: 'var(--text-secondary)', fontWeight: 500
                                            }}>{d}</span>
                                        ))}
                                    </div>
                                    <span style={{ fontSize: '0.73rem', color: 'var(--text-muted)' }}>{emp.email}</span>
                                </div>
                            </div>
                        ) : (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.84rem', fontStyle: 'italic' }}>No employee assigned</span>
                        )}
                    </div>
                );
            }
        },
        {
            header: 'Access Permissions',
            key: 'permissions' as any,
            width: '38%',
            render: (level) => {
                const isStep = level.parent_id === 'none' || level.parent_id === null;

                if (isStep) {
                    // Show SLA for steps here instead of empty
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Clock size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                Time Limit: <strong style={{ color: 'var(--text-secondary)' }}>{level.sla_hours || 12} hours</strong>
                            </span>
                        </div>
                    );
                }

                if (!level.permissions) {
                    return <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', fontStyle: 'italic' }}>No permissions set</span>;
                }

                const activePerms = permConfig.filter(({ key }) => level.permissions![key]);

                if (activePerms.length === 0) {
                    return <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', fontStyle: 'italic' }}>No access granted</span>;
                }

                return (
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                        {activePerms.map(({ key, label, color, bg }) => (
                            <span key={key} style={{
                                fontSize: '0.7rem', fontWeight: 600, padding: '2px 9px', borderRadius: '10px',
                                border: `1px solid ${color}55`,
                                background: bg,
                                color: color,
                                cursor: 'default'
                            }}>
                                {label}
                            </span>
                        ))}
                    </div>
                );
            }
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '140px',
            width: '140px',
            render: (level) => {
                const isSubnode = level.parent_id !== 'none' && level.parent_id !== null;
                return (
                    <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                        {!isSubnode && (
                            <button
                                className="table-action-btn primary"
                                onClick={(e) => { e.stopPropagation(); openModal('sublevel', level.id); }}
                                title="Assign Employee"
                            >
                                <Plus size={18} />
                            </button>
                        )}
                        <button
                            className="table-action-btn"
                            onClick={(e) => { e.stopPropagation(); handleEditClick(level); }}
                            title={isSubnode ? "Edit Employee Assignment" : "Edit Step"}
                        >
                            <Pencil size={18} />
                        </button>
                        <button
                            className="table-action-btn danger"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(level); }}
                            title={isSubnode ? "Remove Employee Assignment" : "Delete Step"}
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                );
            }
        }
    ];

    const totalLevelsCount = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalLevelsCount / pageSize));
    const paginatedLevels = sortedData.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Workflows', path: `/${schoolSlug}/school-admin/workflows`, icon: <Network size={14} /> },
                    { label: departmentName || 'Department', icon: <LayoutGrid size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title={workflowName || 'Workflow Configuration'}
                subtitle="Design sequential approval steps and assign responsible employees."
                icon={<BarChart size={24} />}
                actionLabel="Add Step"
                onActionClick={() => openModal('step')}
                secondaryActionLabel="Preview"
                secondaryActionIcon={<Eye size={16} />}
                onSecondaryActionClick={() => navigate(`/${schoolSlug}/school-admin/workflows/${typeId}/view`)}
            />

            <div style={{ marginTop: '1.25rem' }}>
                <CustomTable
                    columns={columns}
                    data={paginatedLevels}
                    isLoading={isLoading}
                    isCompact={true}
                    emptyMessage="No levels mapped yet."
                    emptyIcon={<BarChart size={32} />}
                    pagination={{
                        currentPage: page,
                        totalPages: totalPages,
                        totalCount: totalLevelsCount,
                        pageSize: pageSize,
                        onPageChange: setPage,
                        hasPrevious: page > 1,
                        hasNext: page < totalPages,
                        isLoading: isLoading,
                        itemName: 'steps & levels',
                    }}
                />
            </div>

            <CustomModal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                title={modalConfig.isEditing
                    ? `Edit ${modalConfig.type === 'step' ? 'Step' : 'Employee'}`
                    : `Add ${modalConfig.type === 'step' ? 'New Step' : 'Employee'}`}
                size={modalConfig.type === 'step' ? "medium" : "large"}
            >
                <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {modalConfig.type === 'step' && (
                            <>
                                <CustomInput
                                    label="Step Name"
                                    placeholder="Step name"
                                    leftIcon={<Tag size={16} />}
                                    error={errors.name?.message || formErrors.name}
                                    {...register('name')}
                                    required
                                />
                                <CustomInput
                                    label="Description"
                                    placeholder="Describe the purpose of this step"
                                    leftIcon={<BarChart size={16} />}
                                    error={errors.role?.message || formErrors.role}
                                    {...register('role')}
                                    required
                                />
                            </>
                        )}
                        {modalConfig.type === 'step' && (
                            <CustomInput
                                label="Resolution Time (Hours)"
                                type="number"
                                placeholder="e.g. 24"
                                leftIcon={<Clock size={16} />}
                                error={errors.sla_hours?.message}
                                {...register('sla_hours')}
                                required
                            />
                        )}
                        {modalConfig.type === 'sublevel' && (
                            <>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <Controller
                                        name="employee"
                                        control={control}
                                        render={({ field }) => (
                                            <CustomSearchableSelect
                                                label="Assign Employee"
                                                placeholder="Search and select employee..."
                                                options={employees.map(emp => ({
                                                    label: emp.designation ? `${emp.name} (${emp.designation})` : emp.name,
                                                    value: emp.id,
                                                    image: emp.photo_url
                                                }))}
                                                leftIcon={<User size={16} />}
                                                error={errors.employee?.message || formErrors.employee}
                                                value={field.value}
                                                onChange={(val) => {
                                                    field.onChange(val);
                                                    setFormErrors(prev => ({ ...prev, employee: '' }));
                                                }}
                                                required
                                            />
                                        )}
                                    />
                                </div>
                                <div style={{ gridColumn: 'span 2' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <Shield size={16} color="var(--accent-primary)" />
                                            <h4 style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem' }}>
                                                Employee Permissions <span style={{ color: '#ef4444' }}>*</span>
                                            </h4>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setValue('permissions.read', true);
                                                    setValue('permissions.forward', true);
                                                    setValue('permissions.resolve', true);
                                                    setValue('permissions.reply', true);
                                                    setValue('permissions.reopen', true);
                                                    setFormErrors(prev => ({ ...prev, permissions: '' }));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: 'var(--accent-primary, #4f46e5)',
                                                    fontSize: '0.78rem',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    transition: 'background 0.2s'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-tertiary, rgba(0,0,0,0.05))'}
                                                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                            >
                                                Select All
                                            </button>
                                            <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>|</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setValue('permissions.read', false);
                                                    setValue('permissions.forward', false);
                                                    setValue('permissions.resolve', false);
                                                    setValue('permissions.reply', false);
                                                    setValue('permissions.reopen', false);
                                                    setFormErrors(prev => ({ ...prev, permissions: 'At least one permission must be selected' }));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: 'var(--text-muted, #6b7280)',
                                                    fontSize: '0.78rem',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    transition: 'background 0.2s'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-tertiary, rgba(0,0,0,0.05))'}
                                                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '1rem',
                                        background: 'var(--bg-tertiary)',
                                        padding: '1.25rem',
                                        borderRadius: '8px',
                                        border: '1px solid var(--border-color)'
                                    }}>
                                        {['read', 'forward', 'resolve', 'reply', 'reopen'].map((perm) => (
                                            <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    {...register(`permissions.${perm}` as any)}
                                                    onChange={(e) => {
                                                        register(`permissions.${perm}` as any).onChange(e);
                                                        setFormErrors(prev => ({ ...prev, permissions: '' }));
                                                    }}
                                                    style={{ accentColor: 'var(--accent-primary)', width: '18px', height: '18px' }}
                                                />
                                                <span style={{ textTransform: 'capitalize', fontSize: '0.9rem', fontWeight: 500 }}>Can {perm}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {formErrors.permissions && (
                                        <p style={{ color: '#ef4444', fontSize: '0.82rem', marginTop: '0.5rem', fontWeight: 500 }}>
                                            {formErrors.permissions}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                        <CustomButton variant="outline" color="secondary" type="button" onClick={closeModal} disabled={isLoading}>Cancel</CustomButton>
                        <CustomButton variant="filled" color="primary" type="submit" loading={isLoading}>
                            {modalConfig.isEditing
                                ? 'Save Changes'
                                : (modalConfig.type === 'step' ? 'Add Step' : 'Assign Employee')}
                        </CustomButton>
                    </div>
                </form>
            </CustomModal>

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={handleConfirmDelete}
                title={deleteModal.type === 'step' ? 'Delete Step' : 'Remove Employee Assignment'}
                message={`Are you sure you want to ${deleteModal.type === 'step' ? `delete this step "${deleteModal.item?.name}"? Deleting a step will also remove all its employee assignments.` : `remove the employee assignment for "${deleteModal.item?.assigned_employee?.name || deleteModal.item?.name}"?`} This action cannot be undone.`}
                confirmLabel="Delete"
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};
